import { useCallback } from "react";

import {
  CRM_PARAGON_MIGRATED,
  CRM_SALESFORCE_MIGRATED,
  DVCVariableTypes,
  DYNAMICS_CRM,
  useVariableValue,
  ZOHO_CRM,
} from "lib/featureFlags";
import { AuthMode, FindProviderResponse } from "lib/generated/integration-api";
import { ParagonProviderType } from "lib/hooks/api/integrations/useRecordParagonInstall";
import { AuthenticatedConnectUser } from "lib/integrations/paragon";
import { CrmRecordKind, LookupTargets } from "lib/integrations/paragonCrm/recordFields";

/** Reads a path from the connected CRM's own API, through the Paragon Proxy. */
export type AskTheCrm = <T>(path: string) => Promise<T | undefined>;

/** Flags that gate a rollout, i.e. the boolean ones. */
type BooleanFlagKey = {
  [K in keyof DVCVariableTypes]: DVCVariableTypes[K] extends boolean ? K : never;
}[keyof DVCVariableTypes];

/**
 * A CRM connected through Paragon rather than an OAuth app of ours. Paragon supplies the name and
 * icon for the connect card, so what a provider has to declare here is only what our own side
 * needs: which key to address it by, what to record the install as, which flag decides who is
 * offered it, and how its own schema names the things a mapping points at.
 */
export type ParagonCrmProvider = {
  /** Key Paragon reports in `getIntegrationMetadata()`. Note the camelCase. */
  integrationKey: string;
  providerType: ParagonProviderType;
  /** How the CRM is named to the user, in copy and toasts. */
  name: string;
  flag: BooleanFlagKey;
  /**
   * What to call the object being created, article included — "a deal", "an opportunity". The
   * mapping carries the CRM's own API name, which reads badly to a customer ("a deals record"),
   * and the article has to travel with the noun because it is not always the same one.
   */
  objectNoun: (objectType: string) => string;
  lookupTargets: LookupTargets;
  /**
   * The object each record is mapped to until the company picks another. Only a starting point —
   * a lead and a bid are both stages of the same pursuit, so both start at the CRM's opportunity,
   * and a contact is a person either way.
   */
  defaultObjects: Record<CrmRecordKind, string>;
  /**
   * The objects each record may be mapped to, narrower than the provider's own list wherever an
   * object is nonsense for the record. Deliberately duplicates the service's own table: the form
   * filters what it offers, the save is checked again, and a mapping the form would never have
   * produced is refused on every push.
   */
  mappableObjects: Record<CrmRecordKind, string[]>;
  /**
   * What a section is filled in with before anyone has configured it, keyed by record and then by
   * the CRM object it is going to. Both are needed: the same object takes different mappings for
   * different records, since a lead's expiry date and a bid's submission date both belong in the
   * one close date an opportunity has.
   *
   * A suggestion, never a promise. Each pair is checked against the customer's own CRM before it
   * reaches the form, and anything the CRM does not have is dropped.
   */
  defaultFields?: Partial<Record<CrmRecordKind, Record<string, Record<string, string>>>>;
  /**
   * Objects this CRM refuses to create without a person. Only a HubSpot lead: it holds no person
   * fields of its own, so nothing in the mapping would otherwise say a person is needed.
   */
  objectsRequiringAPerson?: string[];
  /**
   * Where this company's CRM lives, for a provider whose API never reports it on a push. Zoho needs
   * both halves: the credential names the data centre, the organisation id sits behind the API.
   */
  organisationUrlFrom?: (
    providerData: Record<string, unknown> | undefined,
    ask: AskTheCrm,
  ) => Promise<string | undefined>;
};

/**
 * Each CRM rolls out behind its own flag, so no flag can unlock another CRM: Dynamics and Zoho are
 * greenfield and have `dynamics-crm` and `zoho-crm`; the HubSpot and Salesforce migrations off our
 * own OAuth connector have `crm-paragon-migrated` and `crm-salesforce-migrated`.
 */
export const DYNAMICS_CRM_PROVIDER: ParagonCrmProvider = {
  integrationKey: "microsoftDynamics",
  providerType: "DYNAMICS",
  name: "Microsoft Dynamics",
  flag: DYNAMICS_CRM,
  // Dynamics' own logical names read as the noun already: an opportunity, a contact, a lead.
  objectNoun: (objectType) => `${/^[aeiou]/i.test(objectType) ? "an" : "a"} ${objectType}`,
  lookupTargets: { account: "accounts", owner: "systemusers" },
  defaultObjects: { lead: "opportunity", bid: "opportunity", contact: "contact" },
  mappableObjects: {
    lead: ["opportunity", "lead"],
    bid: ["opportunity"],
    contact: ["contact", "lead"],
  },
  /**
   * An opportunity takes the same fields for both pursuits, bar the date: a lead is tracked to the
   * day it expires and a bid to the day it is submitted, and an opportunity has one estimated
   * close date for either.
   *
   * A contact's number goes to the business phone rather than the mobile one. What a contact
   * carries is a number, not a mobile — Stotles keeps its mobile and direct numbers apart from it.
   *
   * `parentcustomerid` is a customer, which Dynamics means as an account or a contact. It accepts
   * an account, which is what Buyer points at.
   */
  defaultFields: {
    lead: {
      opportunity: {
        title: "name",
        description: "description",
        value: "estimatedvalue",
        expiryDate: "estimatedclosedate",
        stage: "salesstage",
        account: "parentaccountid",
        owner: "ownerid",
      },
    },
    bid: {
      opportunity: {
        title: "name",
        description: "description",
        value: "estimatedvalue",
        submissionDate: "estimatedclosedate",
        stage: "salesstage",
        account: "parentaccountid",
        owner: "ownerid",
      },
    },
    contact: {
      contact: {
        firstName: "firstname",
        lastName: "lastname",
        jobTitle: "jobtitle",
        phone: "telephone1",
        email: "emailaddress1",
        account: "parentcustomerid",
        owner: "ownerid",
      },
    },
  },
};

/**
 * Derived rather than listed, which a new Zoho data centre would leave stale: `www.zohoapis.uk` is
 * `crm.zoho.uk`. Canada is the one that does not follow — its UI is on `zohocloud.ca`.
 */
const CRM_HOST_BY_API_HOST: Record<string, string> = {
  "www.zohoapis.ca": "crm.zohocloud.ca",
};

function crmHostFrom(apiBaseUrl: unknown): string | undefined {
  if (typeof apiBaseUrl !== "string" || !apiBaseUrl) return undefined;
  try {
    const { host } = new URL(apiBaseUrl);
    const named = CRM_HOST_BY_API_HOST[host];
    if (named) return `https://${named}`;
    // Anything else: the API host with its CRM counterpart in front of the same data centre.
    return host.startsWith("www.zohoapis.")
      ? `https://crm.zoho.${host.slice("www.zohoapis.".length)}`
      : undefined;
  } catch {
    return undefined;
  }
}

/** The organisation as Zoho's own URLs name it, which is "org" and its id. */
type ZohoOrg = { org?: { domain_name?: unknown }[] };

/**
 * Zoho addresses a module by one name everywhere — the path, the field metadata, and what a lookup
 * reports as its target — so a reference points at the module itself. Users are the exception: they
 * are not a module, and the API reaches them at a lowercase path of their own.
 */
export const ZOHO_CRM_PROVIDER: ParagonCrmProvider = {
  integrationKey: "zohocrm",
  providerType: "ZOHO",
  name: "Zoho CRM",
  flag: ZOHO_CRM,
  // Every module a mapping can target is plural in Zoho — "Deals", "Contacts".
  objectNoun: (objectType) => `a ${objectType.replace(/s$/, "").toLowerCase()}`,
  lookupTargets: { account: "Accounts", owner: "users" },
  defaultObjects: { lead: "Deals", bid: "Deals", contact: "Contacts" },
  // Zoho keeps pursuits in Deals and people in Contacts, and like Salesforce has a Leads module
  // holding a person.
  mappableObjects: {
    lead: ["Deals", "Leads"],
    bid: ["Deals"],
    contact: ["Contacts", "Leads"],
  },
  /**
   * A deal takes the same fields for both pursuits, bar the date: a lead is tracked to the day it
   * expires and a bid to the day it is submitted, and a deal has one closing date for either.
   *
   * `Title` is Zoho's job title, so that is what fills it. A salutation would need `Salutation`,
   * which is a picklist, and a salutation is typed as text — so it is left alone.
   */
  defaultFields: {
    lead: {
      Deals: {
        title: "Deal_Name",
        description: "Description",
        value: "Amount",
        expiryDate: "Closing_Date",
        stage: "Stage",
        account: "Account_Name",
        owner: "Owner",
      },
    },
    bid: {
      Deals: {
        title: "Deal_Name",
        description: "Description",
        value: "Amount",
        submissionDate: "Closing_Date",
        stage: "Stage",
        account: "Account_Name",
        owner: "Owner",
      },
    },
    contact: {
      Contacts: {
        firstName: "First_Name",
        lastName: "Last_Name",
        jobTitle: "Title",
        phone: "Phone",
        email: "Email",
        account: "Account_Name",
        owner: "Owner",
      },
    },
  },
  /**
   * A Zoho record URL names the data centre and the organisation first, and only connect-time knows
   * both. Read once and stored, so every later link is built without asking again.
   */
  organisationUrlFrom: async (providerData, ask) => {
    const host = crmHostFrom(providerData?.apiBaseUrl);
    if (!host) return undefined;

    const organisation = await ask<ZohoOrg>("org");
    const domain = organisation?.org?.[0]?.domain_name;
    return typeof domain === "string" && domain ? `${host}/crm/${domain}` : undefined;
  },
};

/** HubSpot addresses every object by its collection, which is also what a mapping stores. */
export const HUBSPOT_CRM_PROVIDER: ParagonCrmProvider = {
  integrationKey: "hubspot",
  providerType: "HUBSPOT",
  name: "HubSpot",
  flag: CRM_PARAGON_MIGRATED,
  // Every object a mapping can target is plural in HubSpot's API — "deals", "contacts".
  objectNoun: (objectType) => `a ${objectType.replace(/s$/, "")}`,
  lookupTargets: { account: "companies", owner: "owners" },
  defaultObjects: { lead: "deals", bid: "deals", contact: "contacts" },
  // A HubSpot lead holds no person of its own — its person fields are read-only rollups from a
  // contact it must be associated with — so unlike every other CRM here it is not somewhere a
  // Stotles contact may go.
  mappableObjects: { lead: ["deals", "leads"], bid: ["deals"], contact: ["contacts"] },
  objectsRequiringAPerson: ["leads"],
  /**
   * A deal takes the same fields for both pursuits, bar the date: a lead is tracked to the day it
   * expires and a bid to the day it is submitted, and a deal has one close date for either.
   *
   * A contact fills in almost completely — `job_function` and `seniority` are HubSpot's own
   * properties rather than anything a customer has to add. The employer goes in `company`, the
   * plain text one, which is not the same thing as the association `Buyer` makes.
   */
  defaultFields: {
    lead: {
      deals: {
        title: "dealname",
        description: "description",
        value: "amount",
        expiryDate: "closedate",
        stage: "dealstage",
        account: "accountId",
        owner: "hubspot_owner_id",
      },
    },
    bid: {
      deals: {
        title: "dealname",
        description: "description",
        value: "amount",
        submissionDate: "closedate",
        stage: "dealstage",
        account: "accountId",
        owner: "hubspot_owner_id",
      },
    },
    contact: {
      contacts: {
        firstName: "firstname",
        lastName: "lastname",
        jobTitle: "jobtitle",
        jobFunction: "job_function",
        phone: "phone",
        email: "email",
        seniority: "seniority",
        buyerName: "company",
        account: "accountId",
        owner: "hubspot_owner_id",
      },
    },
  },
};

/** Salesforce names an object rather than a collection, so a reference points at the object. */
export const SALESFORCE_CRM_PROVIDER: ParagonCrmProvider = {
  integrationKey: "salesforce",
  providerType: "SALESFORCE",
  name: "Salesforce",
  flag: CRM_SALESFORCE_MIGRATED,
  objectNoun: (objectType) =>
    /^opportunity$/i.test(objectType) ? "an opportunity" : `a ${objectType.toLowerCase()}`,
  lookupTargets: { account: "Account", owner: "User" },
  defaultObjects: { lead: "Opportunity", bid: "Opportunity", contact: "Contact" },
  // A Lead is a person, so both a Stotles lead and a contact may become one. A bid may not: it is
  // a pursuit well past the point where an unqualified prospect is the right object.
  mappableObjects: {
    lead: ["Opportunity", "Lead"],
    bid: ["Opportunity"],
    contact: ["Contact", "Lead"],
  },
  /**
   * An opportunity takes the same fields for both pursuits, bar the date: a lead is tracked to the
   * day it expires and a bid to the day it is submitted, and `CloseDate` is the only date an
   * opportunity has. A bid's other three dates and a contact's job function, seniority and employer
   * name have no standard Salesforce field, so they are left for the customer.
   *
   * `Title` is Salesforce's job title, so that is what fills it. A Stotles salutation would need
   * `Salutation`, which is a picklist, and a salutation is typed as text — so it is left alone
   * rather than put somewhere it does not belong.
   */
  defaultFields: {
    lead: {
      Opportunity: {
        title: "Name",
        description: "Description",
        value: "Amount",
        expiryDate: "CloseDate",
        stage: "StageName",
        account: "AccountId",
        owner: "OwnerId",
      },
    },
    bid: {
      Opportunity: {
        title: "Name",
        description: "Description",
        value: "Amount",
        submissionDate: "CloseDate",
        stage: "StageName",
        account: "AccountId",
        owner: "OwnerId",
      },
    },
    contact: {
      Contact: {
        firstName: "FirstName",
        lastName: "LastName",
        jobTitle: "Title",
        email: "Email",
        phone: "Phone",
        account: "AccountId",
        owner: "OwnerId",
      },
    },
  },
  /**
   * Every Salesforce REST response carries relative URLs, so the org's host is not discoverable
   * from a push the way HubSpot's portal is. Paragon has it from the token exchange and reports it
   * on the connected user, so it is read at connect and sent with the connection instead.
   */
  organisationUrlFrom: async (providerData) =>
    typeof providerData?.instanceUrl === "string" && providerData.instanceUrl
      ? providerData.instanceUrl
      : undefined,
};

/**
 * The CRMs that reach Paragon with no native connector of ours behind them, in the order the
 * connect page offers them. Each carries its own flag: there is nobody to migrate, so no shared
 * rollout can speak for them and none can unlock another.
 */
export const GREENFIELD_PARAGON_CRM_PROVIDERS: ParagonCrmProvider[] = [
  DYNAMICS_CRM_PROVIDER,
  ZOHO_CRM_PROVIDER,
];

/**
 * The CRMs a company is moved off our own OAuth connector onto Paragon, in the order the connect
 * page offers them.
 */
export const MIGRATED_PARAGON_CRM_PROVIDERS: ParagonCrmProvider[] = [
  HUBSPOT_CRM_PROVIDER,
  SALESFORCE_CRM_PROVIDER,
];

/** Every CRM reached through Paragon, whether or not it replaces a native connector. */
export const PARAGON_CRM_PROVIDERS: ParagonCrmProvider[] = [
  ...GREENFIELD_PARAGON_CRM_PROVIDERS,
  ...MIGRATED_PARAGON_CRM_PROVIDERS,
];

/**
 * Whether the current user is offered a Paragon CRM, by that CRM's own flag. Every flag is read up
 * front because a hook cannot be called per entry while iterating the registry.
 */
export function useIsParagonCrmEnabled(): (provider: ParagonCrmProvider) => boolean {
  const dynamics = useVariableValue(DYNAMICS_CRM, false);
  const hubspot = useVariableValue(CRM_PARAGON_MIGRATED, false);
  const salesforce = useVariableValue(CRM_SALESFORCE_MIGRATED, false);
  const zoho = useVariableValue(ZOHO_CRM, false);

  return useCallback(
    (provider: ParagonCrmProvider) => {
      const enabledByFlag: Partial<Record<BooleanFlagKey, boolean>> = {
        [DYNAMICS_CRM]: dynamics,
        [CRM_PARAGON_MIGRATED]: hubspot,
        [CRM_SALESFORCE_MIGRATED]: salesforce,
        [ZOHO_CRM]: zoho,
      };
      return enabledByFlag[provider.flag] ?? false;
    },
    [dynamics, hubspot, salesforce, zoho],
  );
}

/**
 * The CRM an install is for, or nothing when the install is one we run OAuth for — those have no
 * Paragon connection and configure what they push through their own setup page instead.
 *
 * The auth mode is what decides it, not the type: HubSpot and Salesforce are reachable both ways,
 * so matching on the type alone would hand a native install a Paragon CRM and offer it a mapping
 * form whose save the service then refuses.
 */
export function paragonCrmProviderFor(
  install: Pick<FindProviderResponse, "providerType" | "authMode"> | undefined,
): ParagonCrmProvider | undefined {
  if (install?.authMode !== AuthMode.Paragon) return undefined;
  return PARAGON_CRM_PROVIDERS.find((provider) => provider.providerType === install.providerType);
}

/** The organisation URL to record with a connection, for a CRM whose API never reports one. */
export async function readOrganisationUrl(
  provider: ParagonCrmProvider,
  user: AuthenticatedConnectUser | undefined,
  ask: AskTheCrm,
): Promise<string | undefined> {
  if (!provider.organisationUrlFrom) return undefined;
  const integration = user?.integrations?.[provider.integrationKey];
  return provider.organisationUrlFrom(integration?.providerData as Record<string, unknown>, ask);
}
